import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LANGUAGES } from "@/i18n";
import { Globe, Check, ChevronDown } from "lucide-react";

const FLAG_EMOJI = { GB: "EN", ES: "ES", FR: "FR", PT: "PT", KE: "SW" };

export default function LanguageSelector({ compact = false, dropUp = false }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const current = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  const changeLanguage = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref} data-testid="language-selector">
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-2 rounded-xl transition-all ${
          compact
            ? "px-3 py-2 text-sm text-slate-600 hover:bg-slate-50"
            : "px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 border border-slate-200 bg-white"
        }`}
        data-testid="language-selector-btn"
      >
        <Globe className="w-4 h-4" />
        <span className="font-medium">{compact ? FLAG_EMOJI[current.flag] : current.name}</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className={`absolute ${dropUp ? "bottom-full mb-1" : "top-full mt-1"} left-0 w-48 bg-white border border-slate-200 rounded-xl shadow-lg z-50 py-1 overflow-hidden`}
          data-testid="language-dropdown"
        >
          {LANGUAGES.map((lang) => (
            <button
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                i18n.language === lang.code
                  ? "bg-violet-50 text-violet-700"
                  : "text-slate-600 hover:bg-slate-50"
              }`}
              data-testid={`lang-option-${lang.code}`}
            >
              <span className="w-6 text-center font-semibold text-xs">{FLAG_EMOJI[lang.flag]}</span>
              <span className="flex-1 text-left">{lang.name}</span>
              {i18n.language === lang.code && <Check className="w-4 h-4 text-violet-600" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
